<template>
  <div>
    <div id="location-progress-bar-chart">
      <apexchart type="bar" :height="chartHeight" :options="barChartOptions" :series="series" />
    </div>
    <div class="chart-legend">
      <div class="legend-item">
        <span class="legend-swatch swatch-low"></span>
        <span>0% - 32%</span>
      </div>
      <div class="legend-item">
        <span class="legend-swatch swatch-mid"></span>
        <span>33% - 65%</span>
      </div>
      <div class="legend-item">
        <span class="legend-swatch swatch-high"></span>
        <span>66% - 100%</span>
      </div>
    </div>
    <EnterpriseAdminOverlaySidebar
      :isModalVisible="isModalVisible" 
      @close="closeSidebar"
      ref="location_sidebar" 
      :readOnly="isReadOnly" 
    />

  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import EnterpriseAdminOverlaySidebar from './enterpriseadmin_overlay_sidebar.vue';

export default {
  props: {
    locationNames: {
      type: Array,
      default() {
        return [];
      },
    },
    locations: {
      type: Array,
      default() {
        return [];
      },
    },
    seriesValues: {
      type: Array,
      default() {
        return [];
      },
    },
    seriesName: {
      type: String,
      default: 'Mentee Progress',
    },

  },
  data() {
    return {
      isModalVisible: false,
    }
  },
  methods: {
    goToLocation(index) {
      var accountId = this.locations[index].accountId;
      this.openSidebar(accountId);
    },
    openSidebar(accountId) {
      this.isModalVisible = true;
      this.$refs.location_sidebar.getLocationDetail(accountId);
    },
    closeSidebar() {
      this.isModalVisible = false;
      this.$refs.location_sidebar.selectedComponent = 'OverviewTab';
    },
  },
  computed: {
    isReadOnly(){
      if (this.$store.state.enterprise_type === 'Readonly') {
        return true;
      } else {
        if (this.$store.state.switch_admin_enterprise_isreadonly) {
          return true;
        } else {
          return false;
        }
      }
    },
    resolvedSeriesValues() {
      return this.seriesValues.map((value) => {
        const numericValue = Number(value);
        if (Number.isNaN(numericValue)) {
          return 0;
        }
        if (numericValue < 0) {
          return 0;
        }
        if (numericValue > 100) {
          return 100;
        }
        return numericValue;
      });
    },
    resolvedLocationNames() {
      const fallbackPrefix = 'Location ';
      return this.resolvedSeriesValues.map((_, index) => {
        return this.locationNames[index];
      });
    },
    series() {
      return [
        {
          name: this.seriesName,
          data: this.resolvedSeriesValues,
        },
      ];
    },
    chartHeight() {
      const minRows = 1;
      const rowHeight = 36;
      const verticalPadding = 40;
      const rowCount = Math.max(this.resolvedLocationNames.length, minRows);
      let height = rowCount * rowHeight + verticalPadding;
      if (height < 220) {
        height = 220;
      }
      
      return height;
    },
    barChartOptions() {
      const getColor = (val) => {
        if (val >= 0 && val <= 32) return '#EE4200';
        if (val >= 33 && val <= 65) return '#f4d454';
        return '#009118';
      };
      const self = this
      return {
        chart: {
          id: 'location_bar_chart',
          type: 'bar',
          height: this.chartHeight,
          zoom: {
            enabled: false,
          },
          toolbar: {
            show: false,
          },
          events: {
            // Click on a bar
            dataPointSelection(_event, _ctx, config) {
              self.goToLocation(config.dataPointIndex)
            },
            // Click on the category label (y-axis since layout is horizontal)
            xAxisLabelClick(_event, _ctx, config) {
              self.goToLocation(config.labelIndex)
            },
          },
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 4,
            barHeight: '25px',
            dataLabels: {
              position: 'top',
            },
            colors: {
              ranges: [
                {
                  from: 0,
                  to: 32,
                  color: '#EE4200',
                },
                {
                  from: 33,
                  to: 65,
                  color: '#f4d454',
                },
                {
                  from: 66,
                  to: 100,
                  color: '#009118',
                },
              ],
            },
          },
        },
        dataLabels: {
          enabled: true,
          offsetX: 10,
          textAnchor: 'start',
          formatter(value) {
            return value + '%';
          },
          style: {
            colors: ['#1f2937'],
          },
        },
        xaxis: {
          min: 0,
          max: 100,
          tickAmount: 4,
          categories: this.resolvedLocationNames,
          labels: {
            formatter(value) {
              return value + '%';
            },
          },
        },
        yaxis: {
          labels: {
            // Make the y-axis (category) labels look clickable
            style: { cssClass: 'apex-axis-label-clickable' },
          },
        },
        grid: {
          padding: {
            right: 24,
          },
          strokeDashArray: 4,
          xaxis: {
            lines: {
              show: true,
            },
          },
          yaxis: {
            lines: {
              show: true,
            },
          },
        },
        legend: {
          show: false,
        },
        fill: {
          opacity: 1
        },
        states: {
          hover: {
            filter: {
              type: 'none',
            },
          },
          active: {
            filter: {
              type: 'none',
            },
          },
        },
        tooltip: {
          shared: true,
          intersect: false,
          fixed: {
            enabled: false,
            position: 'topRight' // try: topLeft, bottomRight
          },
          custom: function({ series, dataPointIndex, w }) {
            const progress = series[0][dataPointIndex];
            const label = w.globals.labels[dataPointIndex];
            const color = getColor(progress);
            let left = '0px';
            if (progress > 80) {
              left = '-250px';
            }
            else if (progress > 50) {
              left = '-150px';
            }
            return `
              <div style="padding:8px; font-size: 13px; position: absolute; z-index: 9999; top: -40px; left: ${left}; background-color: #FFF;">
                <div style="font-size: 15px;"><strong>${label}</strong></div>
                <div style="background: ${color};width: 10px; height: 10px;display: inline-block;border-radius: 0;"></div> Mentee Progress: ${progress}%<br />
            `;
          }
        },
      };
    },
  },
  components: {
    apexchart: VueApexCharts,
    EnterpriseAdminOverlaySidebar,
  },
};
</script>

<style scoped>
.chart-legend {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 12px;
  flex-wrap: wrap;
}

.legend-item {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #374151;
}

.legend-swatch {
  width: 10px;
  height: 10px;
  margin-right: 8px;
  display: inline-block;
}

.swatch-low {
  background: #EE4200;
}

.swatch-mid {
  background: #f4d454;
}

.swatch-high {
  background: #009118;
}
.apex-axis-label-clickable {
  cursor: pointer;
}
.apex-axis-label-clickable:hover {
  text-decoration: underline;
}
/* Bars also get a pointer cursor */
.apexcharts-bar-area {
  cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// src/components/enterpriseadmin/location_progress_barchart.vue